/**
 * Marketing pricing helpers for package user capacity and annual discount math.
 * Prices are flat per-organization; each tier includes a max user count.
 *
 * Note: user-capacity limits here are marketing entitlements shown on pricing.
 * Runtime package enforcement in the API is module-based (allowedModules), not
 * a hard user-count cap.
 */

export type PackageSeatInfo = {
  /** Max active organization users on the tenant; null = unlimited */
  maxUsers: number | null;
  label: string;
  hrUsers: string;
  financeUsers: string;
  managers: string;
  storage: string;
};

export const PACKAGE_SEATS: Record<string, PackageSeatInfo> = {
  STARTER: {
    maxUsers: 25,
    label: "Up to 25 users",
    hrUsers: "2 HR Admins",
    financeUsers: "1 Finance User",
    managers: "5 Department Managers",
    storage: "10 GB Cloud Storage",
  },
  STANDARD: {
    maxUsers: 100,
    label: "Up to 100 users",
    hrUsers: "5 HR Admins",
    financeUsers: "3 Finance Users",
    managers: "15 Department Managers",
    storage: "50 GB Cloud Storage",
  },
  BUSINESS: {
    maxUsers: 500,
    label: "Up to 500 users",
    hrUsers: "15 HR Admins",
    financeUsers: "10 Finance Users",
    managers: "50 Department Managers",
    storage: "250 GB Cloud Storage",
  },
  ENTERPRISE: {
    maxUsers: null,
    label: "Unlimited users",
    hrUsers: "Unlimited HR Admins",
    financeUsers: "Unlimited Finance Users",
    managers: "Unlimited Managers",
    storage: "1 TB / Dedicated Cloud Instance",
  },
};

/** Display copy overrides — keep API/DB descriptions intact; refine UX on the page. */
export const PACKAGE_POSITIONING: Record<
  string,
  { tagline?: string; description?: string; valueLine?: string }
> = {
  STANDARD: {
    tagline: "Best for growing businesses",
  },
  BUSINESS: {
    description:
      "Complete ERP for growing businesses with Finance, CRM, Inventory, Procurement, and Payroll.",
  },
  ENTERPRISE: {
    valueLine: "Unlimited users · All modules included",
    description:
      "Unlimited users with Manufacturing, Quality, ATS, Operations, BI, and every Business module.",
  },
};

export function getPackageSeats(code: string): PackageSeatInfo {
  return PACKAGE_SEATS[code.toUpperCase()] || PACKAGE_SEATS.STANDARD;
}

export function getPackagePositioning(code: string) {
  return PACKAGE_POSITIONING[code.toUpperCase()] || {};
}

/** True when a package can host `teamSize` users. */
export function packageFitsTeam(code: string, teamSize: number): boolean {
  const seats = getPackageSeats(code);
  if (seats.maxUsers == null) return true;
  return teamSize <= seats.maxUsers;
}

/**
 * Recommend the lowest-capacity package that fits the team size.
 * Falls back to ENTERPRISE when nothing else fits (including 1000+).
 */
export function recommendPackageCode(
  packages: { code: string; monthlyPrice: number }[],
  teamSize: number,
): string | null {
  const fitting = packages
    .filter((p) => packageFitsTeam(p.code, teamSize))
    .sort((a, b) => {
      const aMax = getPackageSeats(a.code).maxUsers ?? Number.POSITIVE_INFINITY;
      const bMax = getPackageSeats(b.code).maxUsers ?? Number.POSITIVE_INFINITY;
      if (aMax !== bMax) return aMax - bMax;
      return a.monthlyPrice - b.monthlyPrice;
    });
  return (
    fitting[0]?.code ??
    packages.find((p) => p.code.toUpperCase() === "ENTERPRISE")?.code ??
    null
  );
}

/**
 * Annual discount vs paying monthly for 12 months.
 * Returns null when yearly is not cheaper (or monthly is 0).
 */
export function annualDiscountPercent(monthlyPrice: number, yearlyPrice: number): number | null {
  const annualizedMonthly = monthlyPrice * 12;
  if (annualizedMonthly <= 0 || yearlyPrice <= 0) return null;
  if (yearlyPrice >= annualizedMonthly) return null;
  return ((annualizedMonthly - yearlyPrice) / annualizedMonthly) * 100;
}

/** Rounded whole-number discount for UI badges (e.g. 16.68 → 17). */
export function formatAnnualSaveBadge(percent: number): string {
  return `Save ${Math.round(percent)}%`;
}

/**
 * Effective monthly amount shown on cards.
 * Annual mode shows yearly ÷ 12 (rounded); monthly mode shows monthlyPrice.
 */
export function effectiveMonthlyPrice(
  monthlyPrice: number,
  yearlyPrice: number,
  isYearly: boolean,
): number {
  return isYearly ? Math.round(yearlyPrice / 12) : monthlyPrice;
}

/**
 * Max annual discount across a list of packages — used for the billing toggle badge.
 */
export function maxAnnualDiscountPercent(
  packages: { monthlyPrice: number; yearlyPrice: number }[],
): number | null {
  let max: number | null = null;
  for (const pkg of packages) {
    const pct = annualDiscountPercent(pkg.monthlyPrice, pkg.yearlyPrice);
    if (pct == null) continue;
    if (max == null || pct > max) max = pct;
  }
  return max;
}

/** Slider presets for the interactive team-size control. */
export const TEAM_SIZE_PRESETS = [10, 25, 50, 100, 250, 500, 1000] as const;

/**
 * Capabilities that apply to every SaaS tier (verified from product surfaces).
 * Avoid unverified SLA / backup-frequency / support-tier claims.
 */
export const EVERY_PLAN_INCLUDES = [
  "Cloud web access",
  "Secure multi-tenant organization isolation",
  "Role-based access control",
  "Employee self-service (My Portal)",
  "Plan upgrades available anytime",
] as const;
